import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

// Convex URL from environment variable or config
const CONVEX_URL = process.env.REACT_APP_CONVEX_URL || '';

// Add type declaration for convex on window
declare global {
  interface Window {
    convex?: {
      setAuth: (tokenOrProvider: string | (() => Promise<string>)) => void;
      clearAuth: () => void;
    };
  }
}

interface Role {
  _id: Id<"roles">;
  name: string;
  permissions: string[];
}

interface ConvexUser {
  id: Id<"users">;
  name: string;
  email: string;
  role: Role | null;
}

interface User {
  id: Id<"users">;
  name: string;
  email: string;
  role: {
    name: string;
    permissions: string[];
  };
}

// Transform Convex user to our User interface
const transformUser = (convexUser: ConvexUser): User => ({
  id: convexUser.id,
  name: convexUser.name,
  email: convexUser.email,
  role: convexUser.role ? {
    name: convexUser.role.name,
    permissions: convexUser.role.permissions
  } : { name: '', permissions: [] }
});

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  sessionToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  checkAndRefreshAuth: () => Promise<boolean>;
  // Helper functions for authenticated API calls
  useAuthenticatedQuery: (functionName: string, args?: any) => any;
  useAuthenticatedMutation: (functionName: string) => (args?: any) => Promise<any>;
  useAuthenticatedAction: (functionName: string) => (args?: any) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Move to Auth state constants for clarity
const AUTH_STATES = {
  UNKNOWN: 'unknown',
  AUTHENTICATED: 'authenticated', 
  UNAUTHENTICATED: 'unauthenticated'
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState(AUTH_STATES.UNKNOWN);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  
  // Use a ref to track active intervals so we can clean them up properly
  const refreshIntervalRef = useRef<number | null>(null);
  const sessionCheckIntervalRef = useRef<number | null>(null);
  const initTimeoutRef = useRef<number | null>(null);
  const authInitializedRef = useRef<boolean>(false);

  // Force TypeScript to treat all Convex API calls as string paths to avoid excessive type instantiation
  const loginAction = useAction('auth:login' as any);
  const logoutMutation = useMutation('auth:logout' as any);
  const session = useQuery(
    'auth:validateSession' as any,
    sessionToken ? { token: sessionToken } : "skip"
  );

  // Helper functions for authenticated API calls - these will be custom hooks
  const useAuthenticatedQueryInternal = (functionName: string, args: any = {}) => {
    // Always call useQuery, but conditionally pass "skip"
    const shouldSkip = args === "skip" || !sessionToken;
    
    // List of functions that don't use session-based auth (they use Convex's built-in auth)
    const nonSessionFunctions = [
      'filesFunctions:listFilesByProjectFn',
      'filesFunctions:listFilesByComponentFn',
      'filesFunctions:getFileUrlFn',
      'filesFunctions:generateUploadUrlFn',
      'filesFunctions:saveFileMetadataFn',
      'filesFunctions:deleteFileFn',
      'projects:getBySlug',
      'pageComponents:getComponentsForPublicPage',
      'languages:getByCode',
      'languages:getDefault',
      'languages:getActive',
      'languages:listPublic'
    ];
    
    // Check if this function should not receive sessionToken
    const shouldNotAddSessionToken = nonSessionFunctions.includes(functionName);
    
    let queryArgs;
    if (shouldSkip) {
      queryArgs = "skip";
    } else if (shouldNotAddSessionToken) {
      // For file functions, pass args as-is without adding sessionToken
      queryArgs = typeof args === 'object' && args !== null ? args : {};
    } else {
      // For session-based functions, add sessionToken
      queryArgs = typeof args === 'object' && args !== null ? { ...args, sessionToken } : { sessionToken };
    }
    
    return useQuery(functionName as any, queryArgs);
  };

  const useAuthenticatedMutationInternal = (functionName: string) => {
    const mutation = useMutation(functionName as any);
    return async (args: any = {}) => {
      if (!sessionToken) {
        throw new Error('Not authenticated');
      }
      
      // List of functions that don't use session-based auth
      const nonSessionFunctions = [
        'filesFunctions:generateUploadUrlFn',
        'filesFunctions:saveFileMetadataFn',
        'filesFunctions:deleteFileFn',
        'projects:createTest',
        'projects:deleteProject'
      ];
      
      // Check if this function should not receive sessionToken
      const shouldNotAddSessionToken = nonSessionFunctions.includes(functionName);
      
      if (shouldNotAddSessionToken) {
        // For file functions, pass args as-is without adding sessionToken
        return await mutation(args);
      } else {
        // For session-based functions, add sessionToken
        return await mutation({ ...args, sessionToken });
      }
    };
  };

  const useAuthenticatedActionInternal = (functionName: string) => {
    const action = useAction(functionName as any);
    return async (args: any = {}) => {
      if (!sessionToken) {
        throw new Error('Not authenticated');
      }
      
      // List of functions that don't use session-based auth
      const nonSessionFunctions: string[] = [
        // Add any action functions that don't use session tokens here
      ];
      
      // Check if this function should not receive sessionToken
      const shouldNotAddSessionToken = nonSessionFunctions.includes(functionName);
      
      if (shouldNotAddSessionToken) {
        // For non-session functions, pass args as-is without adding sessionToken
        return await action(args);
      } else {
        // For session-based functions, add sessionToken
        return await action({ ...args, sessionToken });
      }
    };
  };

  // More robust function to set auth token on the Convex client
  const setConvexAuth = async (token: string): Promise<boolean> => {
    if (!token) return false;
    
    if (window.convex) {
      try {
        // Use a more reliable approach with a function that returns a promise
        window.convex.setAuth(() => {
          return Promise.resolve(token);
        });
        
        // Give the token time to be applied
        await new Promise(resolve => setTimeout(resolve, 100));
        return true;
      } catch (error) {
        console.error("Error setting auth token on Convex client:", error);
        return false;
      }
    } else {
      console.error("window.convex is not available");
      return false;
    }
  };

  // Function to clear auth token from Convex client
  const clearConvexAuth = () => {
    if (window.convex) {
      try {
        window.convex.clearAuth();
        console.log("Convex auth token cleared");
      } catch (error) {
        console.error("Error clearing auth token from Convex client:", error);
      }
    }
  };

  // Load stored session on mount
  useEffect(() => {
    const initializeAuth = () => {
      console.log("Initializing authentication...");
      
      const storedToken = localStorage.getItem('sessionToken');
      if (storedToken) {
        console.log("Found stored session token");
        setSessionToken(storedToken);
        
        // Set the token on Convex client immediately
        setConvexAuth(storedToken).then(success => {
          if (success) {
            console.log("Auth token set successfully during initialization");
          }
        });
      } else {
        console.log("No stored session token found");
        setAuthState(AUTH_STATES.UNAUTHENTICATED);
        setIsLoading(false);
      }
      
      authInitializedRef.current = true;
    };

    if (!authInitializedRef.current) {
      initializeAuth();
    }
  }, []);

  // Handle session validation response
  useEffect(() => {
    if (!sessionToken) {
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      setUser(null);
      setIsLoading(false);
      return;
    }
    
    if (session === undefined) {
      // Still loading, but we can proceed with rendering
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      
      // If it's been more than 1 second since we started loading, show the app
      if (isLoading) {
        setTimeout(() => setIsLoading(false), 1000);
      }
      return;
    }
    
    // At this point we have a definitive answer from the session validation
    if (session) {
      // Only log on initial authentication, not on every validation
      if (authState !== AUTH_STATES.AUTHENTICATED) {
        console.log("Session validated successfully");
      }
      
      // Only set auth token if not already authenticated to prevent loops
      if (authState !== AUTH_STATES.AUTHENTICATED) {
        setConvexAuth(sessionToken).then(success => {
          if (success) {
            console.log("Auth token set after session validation");
          }
        });
      }
      
      setAuthState(AUTH_STATES.AUTHENTICATED);
      setUser(transformUser(session.user));
      setIsLoading(false);
    } else {
      console.log("Session validation failed");
      
      // Clear invalid session
      localStorage.removeItem('sessionToken');
      setSessionToken(null);
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      setUser(null);
      setIsLoading(false);
      clearConvexAuth();
    }
  }, [session, sessionToken, isLoading, authState]);

  // Periodic session refresh (every 5 minutes) - disabled to prevent loops
  useEffect(() => {
    // Commenting out periodic refresh as it may cause infinite loops
    // The session validation query will automatically re-run when needed
    // if (authState === AUTH_STATES.AUTHENTICATED && sessionToken) {
    //   if (refreshIntervalRef.current) {
    //     clearInterval(refreshIntervalRef.current);
    //   }
    //   
    //   refreshIntervalRef.current = window.setInterval(() => {
    //     console.log("Periodic session refresh");
    //     // The session query will automatically re-run and update our state
    //   }, 5 * 60 * 1000);
    // }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [authState, sessionToken]);

  // Listen for storage changes (for multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sessionToken') {
        if (e.newValue) {
          console.log("Session token updated in another tab");
          setSessionToken(e.newValue);
          setConvexAuth(e.newValue);
        } else {
          console.log("Session token removed in another tab");
          setSessionToken(null);
          setAuthState(AUTH_STATES.UNAUTHENTICATED);
          setUser(null);
          clearConvexAuth();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log("Login attempt for:", email);
      setIsLoading(true);
      
      const result = await loginAction({ 
        email, 
        password,
        userAgent: navigator.userAgent,
        ipAddress: undefined
      });
      
      if (result.success) {
        const token = result.token;
        console.log("Login successful, setting session token");
        
        // Store token in localStorage
        localStorage.setItem('sessionToken', token);
        
        // Set token on Convex - use the more robust function
        const authSet = await setConvexAuth(token);
        if (!authSet) {
          console.error("Failed to set auth token after login");
        }
        
        setSessionToken(token);
        setAuthState(AUTH_STATES.AUTHENTICATED);
        setUser(transformUser(result.user));
        
        // Dispatch event to notify other tabs
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'sessionToken',
          newValue: token,
          storageArea: localStorage
        }));
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log("Logging out...");
      
      if (sessionToken) {
        await logoutMutation({ token: sessionToken });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all auth state regardless of logout success
      localStorage.removeItem('sessionToken');
      setSessionToken(null);
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      setUser(null);
      setIsLoading(false);
      clearConvexAuth();
      
      // Notify other tabs
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'sessionToken',
        newValue: null,
        storageArea: localStorage
      }));
    }
  };

  const hasPermission = (permission: string) => {
    if (!user || !user.role) return false;
    return user.role.permissions.includes(permission) || user.role.permissions.includes('*');
  };

  const checkAndRefreshAuth = async (): Promise<boolean> => {
    console.log("Checking and refreshing authentication...");
    
    const storedToken = localStorage.getItem('sessionToken');
    if (!storedToken) {
      console.log("No stored token found");
      return false;
    }
    
    try {
      // Force a session validation by setting the token again
      setSessionToken(storedToken);
      const authSet = await setConvexAuth(storedToken);
      
      if (authSet) {
        console.log("Auth refresh successful");
        return true;
      }
    } catch (error) {
      console.error("Error during auth refresh:", error);
    }
    
    return false;
  };

  const contextValue: AuthContextType = {
    isAuthenticated: authState === AUTH_STATES.AUTHENTICATED,
    isLoading,
    user,
    sessionToken,
    login,
    logout,
    hasPermission,
    checkAndRefreshAuth,
    useAuthenticatedQuery: useAuthenticatedQueryInternal,
    useAuthenticatedMutation: useAuthenticatedMutationInternal,
    useAuthenticatedAction: useAuthenticatedActionInternal,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}; 